import { useLocalStorage } from "usehooks-ts";
import {
  DataEditor,
  GridCell,
  GridCellKind,
  GridColumn,
  Item,
} from "@glideapps/glide-data-grid";
import "@glideapps/glide-data-grid/dist/index.css";
import { Client } from "../../external";
import { useCallback, useEffect, useState } from "react";
import {
  DropdownCellType,
  DropdownCell,
  ButtonCellType,
  ButtonCell,
} from "@glideapps/glide-data-grid-cells";
import { AllHeaderIconsRecordForTable } from "../bootstrap-icons";
import SearchCatalogueModal from "./search-catalogue-modal";

/**
 * Specifies fields for an imported manifest template.
 *
 * This interface *must* match with the Import Manifest template
 * that is provided to download.
 */
export interface ImportedManifestEntry {
  productSku: string;
  variant: string;
  palletRef: string;
  retailPricePence: number;
  itemDescription: string;
  productCategory: string;
  quantity: number;
  clientGrade: string;
  clientGradingComments: string;
  inboundDate: string;
  originalOrderRef: string;
  originalReturnRef: string;
  weightGrams: string;
  hyperlink: string;
  packageLengthCM: number;
  packageWidthCM: number;
  packageHeightCM: number;
  productLengthCM: number;
  productWidthCM: number;
  productHeightCM: number;
}

export interface ManifestEntry extends ImportedManifestEntry {
  inventoryId: string;
  quarantine?: boolean;
  shippingService?: string;
  catalogueStatus?:
    | "Searching..."
    | "Matched"
    | "No Match Found"
    | "Possible Match";
}

export interface InboundManifestComponentProps {
  clients: Client[];
  userToken: string;
  manifestId: string;
}

const columns: GridColumn[] = [
  {
    title: "Pallet Number/Reference",
    width: 200,
    group: "Mandatory",
    icon: "CaretDownSquare",
  },
  { title: "Product SKU", width: 150, group: "Mandatory", icon: "Postcard" },
  { title: "Quantity", width: 100, group: "Mandatory", icon: "Boxes" },
  { title: "Product Category", width: 150, group: "Provide if on manifest" },
  { title: "Title", width: 250, group: "Provide if on manifest" },
  {
    title: "RRP",
    width: 100,
    group: "Provide if on manifest",
    icon: "CurrencyPound",
  },
  { title: "Width", width: 80, group: "Package Dims" },
  { title: "Length", width: 80, group: "Package Dims" },
  { title: "Height", width: 80, group: "Package Dims" },
  { title: "Width", width: 80, group: "Product Dims" },
  { title: "Length", width: 80, group: "Product Dims" },
  { title: "Height", width: 80, group: "Product Dims" },
  { title: "Weight", width: 100, group: "Product Dims", icon: "BoxSeam" },
  {
    title: "Quarantine",
    width: 110,
    group: "Outcomes",
    icon: "QuestionCircle",
  },
  { title: "In Catalogue?", width: 150, group: "Outcomes" },
  { title: "Search", width: 100, group: "Outcomes", icon: "Search" },
  { title: "Shipping Service", width: 150, group: "Outcomes", icon: "Truck" },
  { title: "Exit", width: 150, group: "Outcomes", icon: "CurrencyPound" },
];

// Number of empty rows to show when no data is available
const DEFAULT_EMPTY_ROWS = 10;

// Function to create an empty manifest entry
const createEmptyManifestEntry = (): ManifestEntry => ({
  productSku: "",
  variant: "",
  palletRef: "",
  retailPricePence: 0,
  itemDescription: "",
  productCategory: "",
  quantity: 0,
  clientGrade: "",
  clientGradingComments: "",
  inboundDate: "",
  originalOrderRef: "",
  originalReturnRef: "",
  weightGrams: "",
  hyperlink: "",
  packageLengthCM: 0,
  packageWidthCM: 0,
  packageHeightCM: 0,
  productLengthCM: 0,
  productWidthCM: 0,
  productHeightCM: 0,
  inventoryId: "",
  quarantine: false,
  shippingService: "best",
  catalogueStatus: "No Match Found",
});

export const InboundManifestComponent = ({
  clients,
  userToken,
  manifestId,
}: InboundManifestComponentProps) => {
  const [manifestRows, setManifestRows] = useLocalStorage<ManifestEntry[]>(
    `CCManifestRows-${manifestId}`,
    []
  );

  // State for tracking if we're showing empty rows
  const [showingEmptyRows, setShowingEmptyRows] = useState(
    manifestRows.length === 0
  );

  // Add state for the search dialog
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState<number | null>(null);

  // Create empty rows if no data is available
  useEffect(() => {
    if (manifestRows.length === 0) {
      const emptyRows = Array(DEFAULT_EMPTY_ROWS)
        .fill(null)
        .map(() => createEmptyManifestEntry());

      setManifestRows(emptyRows);
      setShowingEmptyRows(true);
    } else {
      setShowingEmptyRows(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add a state to force re-render when data is loaded
  const [, setIsDataReady] = useState(false);

  const headerIcons = AllHeaderIconsRecordForTable;

  // Effect to handle initialization
  useEffect(() => {
    console.log("Loading manifest data from storage:", manifestRows);

    // Mark data as ready for rendering
    if (manifestRows && manifestRows.length > 0) {
      console.log("Data found in local storage, rows:", manifestRows.length);
      setIsDataReady(true);
    } else {
      console.log("No data found in local storage");
    }
  }, [manifestRows]);

  // Function to handle opening the search dialog
  const handleOpenSearchDialog = (rowIndex: number) => {
    setCurrentRowIndex(rowIndex);

    // Set status to "Searching..." when dialog opens
    const newData = [...manifestRows];
    const item = { ...newData[rowIndex] };
    item.catalogueStatus = "Searching...";
    newData[rowIndex] = item;
    setManifestRows(newData);

    setShowSearchDialog(true);
  };

  // Function to handle saving the search results
  const handleSaveSearchResults = (
    newStatus: ManifestEntry["catalogueStatus"]
  ) => {
    if (currentRowIndex !== null) {
      const newData = [...manifestRows];
      const item = { ...newData[currentRowIndex] };

      // Update the catalogue status
      item.catalogueStatus = newStatus;

      newData[currentRowIndex] = item;
      setManifestRows(newData);
    }
  };

  // Helper functions to format values with units and parse them
  const formatWithUnit = (
    value: number | string | undefined,
    unit: string
  ): string => {
    if (value === undefined || value === null || value === 0 || value === "") {
      return "";
    }
    return `${value}${unit}`;
  };

  const parseNumericValue = (value: string): number => {
    const numericValue = parseFloat(value.replace(/[^0-9.]/g, ""));
    return isNaN(numericValue) ? 0 : numericValue;
  };

  // Function to get cell content for DataEditor
  function getData([col, row]: Item): GridCell {
    // Handle case when row is out of bounds or data is not loaded
    if (row >= manifestRows.length) {
      return {
        kind: GridCellKind.Text,
        data: "",
        allowOverlay: true,
        displayData: "",
      };
    }

    const item = manifestRows[row];
    switch (col) {
      case 0: // Pallet Num/Ref
        return {
          kind: GridCellKind.Text,
          data: item.palletRef || "",
          allowOverlay: true,
          displayData: item.palletRef || "",
        };
      case 1: // Product SKU
        return {
          kind: GridCellKind.Text,
          data: item.productSku || "",
          allowOverlay: true,
          displayData: item.productSku || "",
        };
      case 2: // Quantity
        return {
          kind: GridCellKind.Number,
          data: item.quantity || 0,
          allowOverlay: true,
          displayData: item.quantity?.toString() || "0",
        };
      case 3: // Product Category
        return {
          kind: GridCellKind.Text,
          data: item.productCategory || "",
          allowOverlay: true,
          displayData: item.productCategory || "",
        };
      case 4: // Title (Description)
        return {
          kind: GridCellKind.Text,
          data: item.itemDescription || "",
          allowOverlay: true,
          displayData: item.itemDescription || "",
        };
      case 5: // RRP (Retail Price)
        return {
          kind: GridCellKind.Text,
          data: item.retailPricePence
            ? `£${(item.retailPricePence / 100).toFixed(2)}`
            : "",
          allowOverlay: true,
          displayData: item.retailPricePence
            ? `£${(item.retailPricePence / 100).toFixed(2)}`
            : "",
        };
      case 6: // Package Length
        return {
          kind: GridCellKind.Text,
          data: formatWithUnit(item.packageLengthCM, "cm"),
          allowOverlay: true,
          displayData: formatWithUnit(item.packageLengthCM, "cm"),
        };
      case 7: // Package Width
        return {
          kind: GridCellKind.Text,
          data: formatWithUnit(item.packageWidthCM, "cm"),
          allowOverlay: true,
          displayData: formatWithUnit(item.packageWidthCM, "cm"),
        };
      case 8: // Package Height
        return {
          kind: GridCellKind.Text,
          data: formatWithUnit(item.packageHeightCM, "cm"),
          allowOverlay: true,
          displayData: formatWithUnit(item.packageHeightCM, "cm"),
        };
      case 9: // Product Length
        return {
          kind: GridCellKind.Text,
          data: formatWithUnit(item.productLengthCM, "cm"),
          allowOverlay: true,
          displayData: formatWithUnit(item.productLengthCM, "cm"),
        };
      case 10: // Product Width
        return {
          kind: GridCellKind.Text,
          data: formatWithUnit(item.productWidthCM, "cm"),
          allowOverlay: true,
          displayData: formatWithUnit(item.productWidthCM, "cm"),
        };
      case 11: // Product Height
        return {
          kind: GridCellKind.Text,
          data: formatWithUnit(item.productHeightCM, "cm"),
          allowOverlay: true,
          displayData: formatWithUnit(item.productHeightCM, "cm"),
        };
      case 12: // Weight (kg)
        return {
          kind: GridCellKind.Text,
          data: formatWithUnit(item.weightGrams, "kg"),
          allowOverlay: true,
          displayData: formatWithUnit(item.weightGrams, "kg"),
        };
      case 13: // Quarantine
        return {
          kind: GridCellKind.Boolean,
          data: item.quarantine || false,
          allowOverlay: false,
        };
      case 14: // Found in Catalogue
        return {
          kind: GridCellKind.Text,
          data: item.catalogueStatus || "",
          allowOverlay: false,
          displayData: item.catalogueStatus || "",
          themeOverride: item.catalogueStatus
            ? {
                textDark: "#000000",
                bgCell:
                  item.catalogueStatus === "Matched"
                    ? "#d4edda"
                    : item.catalogueStatus === "No Match Found"
                    ? "#f8d7da"
                    : item.catalogueStatus === "Possible Match"
                    ? "#fff3cd"
                    : "#ffffff", // Default to white
              }
            : undefined,
        };
      case 15: // Search Column
        return {
          kind: GridCellKind.Custom,
          data: {
            title: "Search",
            kind: "button-cell",
            label: "Search",
            onClick: () => handleOpenSearchDialog(row),
          },
          readonly: true,
          allowOverlay: false,
          copyData: "Search",
        } as ButtonCellType;
      case 16: // Shipping Service
        return {
          kind: GridCellKind.Custom,
          cursor: "pointer",
          allowOverlay: true,
          copyData: item.shippingService || "best",
          data: {
            kind: "dropdown-cell",
            allowedValues: [
              { value: "best", label: "Best" },
              { value: "standard", label: "Standard" },
              { value: "express", label: "Express" },
            ],
            value: item.shippingService || "best",
          },
        } as DropdownCellType;
      case 17: // Exit
        return {
          kind: GridCellKind.Text,
          data: "Auction",
          allowOverlay: false,
          displayData: "Auction",
        };
      default:
        return {
          kind: GridCellKind.Text,
          data: "",
          allowOverlay: false,
          displayData: "",
        };
    }
  }

  // Handle cell edits
  const onCellEdited = useCallback(
    (cell: Item, newValue: any) => {
      const [col, row] = cell;

      // If we're showing empty rows, convert to real data on first edit
      if (showingEmptyRows) {
        setShowingEmptyRows(false);
      }

      if (row < manifestRows.length) {
        const newData = [...manifestRows];
        const item = { ...newData[row] };

        // Update the appropriate field based on the column
        switch (col) {
          case 0:
            item.palletRef = newValue.data;
            break;
          case 1:
            item.productSku = newValue.data;
            break;
          case 2:
            item.quantity = newValue.data;
            break;
          case 3:
            item.productCategory = newValue.data;
            break;
          case 4:
            item.itemDescription = newValue.data;
            break;
          case 5:
            // Parse currency value
            const numericValue =
              parseFloat(newValue.data.replace(/[^0-9.]/g, "")) * 100;
            item.retailPricePence = isNaN(numericValue) ? 0 : numericValue;
            break;
          case 6:
            item.packageLengthCM = parseNumericValue(newValue.data);
            break;
          case 7:
            item.packageWidthCM = parseNumericValue(newValue.data);
            break;
          case 8:
            item.packageHeightCM = parseNumericValue(newValue.data);
            break;
          case 9:
            item.productLengthCM = parseNumericValue(newValue.data);
            break;
          case 10:
            item.productWidthCM = parseNumericValue(newValue.data);
            break;
          case 11:
            item.productHeightCM = parseNumericValue(newValue.data);
            break;
          case 12:
            item.weightGrams = parseNumericValue(newValue.data).toString();
            break;
          case 13:
            item.quarantine = newValue.data;
            break;
          case 14:
            item.catalogueStatus = newValue.data;
            break;
          case 16:
            item.shippingService = newValue.data.value;
            break;
        }

        newData[row] = item;
        setManifestRows(newData);
      }
    },
    [manifestRows, setManifestRows, showingEmptyRows]
  );

  // Function to add a new row
  const addNewRow = () => {
    const newRow = createEmptyManifestEntry();
    const updatedRows = [...manifestRows, newRow];
    setManifestRows(updatedRows);

    // If we were showing empty rows, we're now showing real data
    if (showingEmptyRows) {
      setShowingEmptyRows(false);
    }
  };

  return (
    <div className="w-100 mt-4 container-fluid d-flex flex-column">
      {/* Table definition */}
      <DataEditor
        smoothScrollX={true}
        smoothScrollY={true}
        allowedFillDirections={"vertical"}
        columns={columns}
        headerIcons={headerIcons}
        rows={Math.max(manifestRows.length, DEFAULT_EMPTY_ROWS)}
        getCellContent={getData}
        onCellEdited={onCellEdited}
        getCellsForSelection={true}
        rowMarkers="number"
        onPaste={true}
        keybindings={{ search: true, downFill: true, rightFill: true }}
        trailingRowOptions={{
          sticky: true,
          tint: true,
          hint: "New row...",
        }}
        onRowAppended={addNewRow}
        customRenderers={[DropdownCell, ButtonCell]}
        editOnType={true}
        fillHandle={true}
        groupHeaderHeight={36}
      />

      <div className="mt-3">
        <small className="text-muted">
          {showingEmptyRows
            ? `Showing ${DEFAULT_EMPTY_ROWS} empty rows. Start editing to add data.`
            : `Loaded ${manifestRows.length} rows.`}
        </small>
      </div>

      {/* Search Modal */}
      {currentRowIndex !== null && (
        <SearchCatalogueModal
          show={showSearchDialog}
          onHide={() => setShowSearchDialog(false)}
          productSku={manifestRows[currentRowIndex]?.productSku || ""}
          itemDescription={manifestRows[currentRowIndex]?.itemDescription || ""}
          initialStatus={
            manifestRows[currentRowIndex]?.catalogueStatus &&
            manifestRows[currentRowIndex]?.catalogueStatus !== "Searching..."
              ? manifestRows[currentRowIndex]?.catalogueStatus
              : "No Match Found"
          }
          onSave={handleSaveSearchResults}
        />
      )}
    </div>
  );
};

export default InboundManifestComponent;
